'use client'
import { createContext, useContext, useEffect, useRef, useState } from 'react'
import { useDebugPdfStorage } from '@/services/localStorageService'

type AppContextType = {
  refreshSidebar: () => void
  refreshVersion: number
  debug: boolean
  setDebug: (debug: boolean) => void
  debugPdf: boolean
  setDebugPdf: (debugPdf: boolean) => void
  isLoading: boolean
  loadingMessage: string
  startLoading: (message?: string) => void
  stopLoading: () => void
}

const AppContext = createContext<AppContextType>({
  refreshSidebar: () => {},
  refreshVersion: 0,
  debug: false,
  setDebug: () => {},
  debugPdf: true,
  setDebugPdf: () => {},
  isLoading: false,
  loadingMessage: '',
  startLoading: () => {},
  stopLoading: () => {},
})

export function AppProvider({ children }: { children: React.ReactNode }) {
  const [refreshVersion, setRefreshVersion] = useState(0)
  const [debug, setDebug] = useState<boolean>(process.env.NEXT_PUBLIC_DEBUG === 'true')

  // Debug PDF ora usa localStorage
  const [debugPdf, setDebugPdf] = useDebugPdfStorage()

  // Forziamo il tipo per evitare errori TypeScript
  const debugPdfBool = debugPdf as boolean
  const setDebugPdfBool = setDebugPdf as (value: boolean) => void

  // Stato per il loading globale
  const [loadingMessage, setLoadingMessage] = useState<string>('')
  const isLoading = !!loadingMessage
  // Timeout ref per cleanup
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)

  const startLoading = (message: string = '') => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
    setLoadingMessage(message)
  }

  const stopLoading = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }
    timeoutRef.current = setTimeout(() => {
      setLoadingMessage('')
      timeoutRef.current = null
    }, 1000)
  }

  const refreshSidebar = () => {
    setRefreshVersion(prev => prev + 1)
  }

  // Cleanup del timeout su unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  return (
    <AppContext.Provider
      value={{
        refreshSidebar,
        refreshVersion,
        debug,
        setDebug,
        debugPdf: debugPdfBool,
        setDebugPdf: setDebugPdfBool,
        isLoading,
        loadingMessage,
        startLoading,
        stopLoading,
      }}
    >
      {children}
    </AppContext.Provider>
  )
}

export const useApp = () => useContext(AppContext)
export const useDebug = () => {
  const { debug, setDebug } = useApp()
  return { debug, setDebug }
}

export const useDebugPdf = () => {
  const { debugPdf, setDebugPdf } = useApp()
  return { debugPdf, setDebugPdf }
}
